// Sentry.js
import { addPinoLogListener, removePinoLogListener } from '../resource/pino.js';
import safeStructuredClone from '../resource/safeStructuredClone.js';
import { LOG_CAPTURE_ALLOWED_NAMESPACES } from '../resource/sentryLogCaptureNames.js';
import {
  getIsInBrowserMainThread,
  getIsInServiceWorker,
} from '../resource/getJsEnvironment.js';

let Sentry = null;
let Sentry2Scope = null; // the second sentry for user bug report
let _sessionId = null;

// The main app injects these loaders at startup (see ../index.js), so this
// file holds no `await import(...)` of its own. The Service Worker imports
// this file; keeping dynamic imports out keeps the SW file stable so it
// doesn't look "new" and reinstall on every deploy.
let sentryLoader = null;
let logMetaLoader = null;

// The main app injects the release tag (from ../resource/releaseInfo.js),
// so the `process.env.TAG_NAME / BRANCH_NAME / SHORT_SHA` reads stay out of
// this file. Those values change every deploy; if they were baked in here,
// the SW (which imports this file) would reinstall on every deploy.
let sentryRelease = null;

/**
 * Set the release tag for Sentry. The main app calls this once before
 * `initSentry()`. The Service Worker never calls it, so SW-side errors
 * just have no release tag — acceptable (SW errors are few).
 * @param {string} release - e.g. `'v3.286.4+abc12345'`.
 * @example
 * import { release } from '../resource/releaseInfo.js';
 * import { setSentryRelease } from '../resource/sentry.js';
 * setSentryRelease(release);
 */
export const setSentryRelease = release => {
  sentryRelease = release;
};

/**
 * Set the loader that lazy-loads @sentry/browser. The main app calls this
 * once before `initSentry()`. The Service Worker never calls it — it uses
 * `self._SENTRY_INSTANCE_` instead.
 * @param {function} loader - `() => Promise<typeof import('@sentry/browser')>`
 * @example
 * import { loadSentryBrowser } from '../resource/loadSentryBrowser.js';
 * import { setSentryLoader } from '../resource/sentry.js';
 * setSentryLoader(loadSentryBrowser);
 */
export const setSentryLoader = loader => {
  sentryLoader = loader;
};

/**
 * Set the loader for mixpanel log meta. The main app calls this once at
 * startup. If it's not set (e.g. in the Service Worker), log meta is `{}`.
 * @param {function} loader - `() => Promise<object>`
 * @example
 * import { loadMixpanelLogMeta } from '../resource/loadMixpanelLogMeta.js';
 * import { setLogMetaLoader } from '../resource/sentry.js';
 * setLogMetaLoader(loadMixpanelLogMeta);
 */
export const setLogMetaLoader = loader => {
  logMetaLoader = loader;
};

// --- Structured Logging config (module-level, updated by RemoteConfigMonitor) ---
const LOG_LEVEL_ORDER = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  fatal: 5,
};
let _logEnabled = process.env.SENTRY_STRUCTURED_LOG_ENABLED !== '0';
let _logLevel =
  LOG_LEVEL_ORDER[process.env.SENTRY_STRUCTURED_LOG_LEVEL] ??
  LOG_LEVEL_ORDER.info;

export const updateSentryLogConfig = ({ isEnabled, level } = {}) => {
  if (isEnabled != null) _logEnabled = !!isEnabled;
  if (level != null && LOG_LEVEL_ORDER[level] != null)
    _logLevel = LOG_LEVEL_ORDER[level];
};

if (getIsInServiceWorker()) {
  Sentry = self._SENTRY_INSTANCE_;
}

export const setSessionId = ({ sessionId }) => {
  _sessionId = sessionId;
  if (Sentry) {
    Sentry.setTag('session_id', _sessionId);
  }

  if (Sentry2Scope) {
    Sentry2Scope.setTag('session_id', _sessionId);
  }
};

const commonSentryConfig = {
  environment: 'default',
  // release comes from setSentryRelease() instead of process.env here, so
  // the SW file stays stable across deploys (see the sentryRelease comment).
  level: 'error',
  sampleRate: 0,
  tracesSampleRate: 0,
  // Ref. https://docs.sentry.io/clients/javascript/tips/
  ignoreErrors: [
    // Device memory full.
    /QuotaExceededError/,
    // Safari 12.2 bug. https://bugs.webkit.org/show_bug.cgi?id=197050
    /Connection to Indexed Database server lost/i,
    // User maybe is in incognito mode.
    /No available storage method found/i,

    // sentry error more than 1k
    /Invalid target for t#trigger; must be a DOM node or evented object/i,
    /Non-Error promise rejection captured with keys: error/i,
    /UnknownError: Attempt to iterate a cursor that doesn't exist/i,
    /AbortError: The transaction was aborted, so the request cannot be fulfilled/i,

    // checkDrmCapability
    /Unable to create or initialize key session/i,
    /Failed to execute/i,
    /Key system configuration is not supported/i,
    /No compatible source was found for this media/i,
    /KeySystem or Minimum API level not met for Widevine EME/i,
    /The media could not be loaded, either because the server or network failed or because the format is not supported/i,

    // pubsub fetch error
    /TypeError: Load failed/i,
    /TypeError: Failed to fetch/i,
    /post to pubsub error/i,

    // Random plugins/extensions
    /top.GLOBALS/i,
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    /originalCreateNotification/i,
    /canvas.contentDocument/i,
    // Facebook borked
    /fb_xd_fragment/i,
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    /bmi_SafeAddOnload/i,
    /EBCallBackMessageReceived/i,
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    /conduitPage/i,

    // Some error from android webview.
    /vivo_checkMediaPausedReason is not defined/i,

    // GTM error
    /<script> failed to load/i,
    /Object captured as promise rejection with keys: error/i,

    // Player
    /VIDEOJS CODE/i,
    /SHAKA-PLAYER CODE/i,
    /Object captured as exception with keys: code, data, message/i,
    /null is not an object/i,
    /AbortError: The play/i,
    /null is not an object (evaluating \/this.player_.getChild\/)/i,
    /The media playback was aborted due to a corruption problem or because the media used features your browser did not support/i,
    /Cannot read properties/i,
    /The request is not allowed by the user agent/i,
    /play() failed because the user didn\/t interact with the document first/i,
    /AbortError: The operation was aborted/i,
    /e\.player_ is null/i,
    /NotAllowedError: play/i,

    // pusher
    /pusher batch response is not ok/i,

    // service worker
    /service worker fallback to main thread/i,

    // React
    /Uncaught Error: Minified React error/i,
    /Unknown root exit status/i,
    /Hydration (failed|mismatch)/i,
    /Text content does not match server-rendered HTML/i,
    /Expected server HTML to contain a matching/i,
    /The server could not finish this Suspense boundary/i,
    /This Suspense boundary received an update before it finished hydrating/i,
    /There was an error while hydrating/i,
    /This root received an early update, before anything was able hydrate/i,
    /The current renderer does not support hydration/i,
    /Prop .* did not match\. Server: .* Client:/i,

    // turnstile
    /turnstile/i,

    // 403 error or 5xx error
    /"status":403|"status":5/i,

    // leaker change style
    /leaker change style/i,

    // firebase
    /Messaging: This browser doesn't support/i,

    // safari ld+json error
    /undefined is not an object \(evaluating 'r\[0\]\["@context"\]'\)/i,
    /undefined is not an object \(evaluating 'r\["@context"\].toLowerCase'\)/i,

    // safari indexedDB error
    /UnknownError: Attempt to .* database without an in-progress transaction/i,

    // byteplus try to log error
    /InvalidStateError: The object is in an invalid state/,

    // leaker tried to do somethings
    /leaker tired to do somethings/i,
  ],
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
  ],
};

const isDebugSentryEnabled = () => {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(location.search);
  return searchParams.get('debug-sentry') === '1';
};

export const initSentry = async ({ sentryConfig = {} } = {}) => {
  if (!Sentry) {
    if (getIsInServiceWorker()) {
      Sentry = self._SENTRY_INSTANCE_;
    } else {
      // Lazy load via main-app-injected loader. See setSentryLoader().
      // Mirrors getStructuredLogger()'s null-guard: if a consumer reaches
      // initSentry() before the main app injects the loader, bail out
      // instead of crashing with `null is not a function`.
      if (!sentryLoader) return null;
      Sentry = await sentryLoader();
    }
  }

  const {
    init,
    BrowserClient,
    defaultStackParser,
    getDefaultIntegrations,
    makeFetchTransport,
    Scope,
    isInitialized,
    browserTracingIntegration,
  } = Sentry;

  if (isInitialized()) return Sentry;

  // main sentry
  const sentryOptions = {
    debug: isDebugSentryEnabled(),
    integrations: [
      ...getDefaultIntegrations(),
      browserTracingIntegration({
        instrumentPageLoad: false,
        instrumentNavigation: false,
        markBackgroundSpan: false,
        enableInp: false,
      }),
    ],
    ...commonSentryConfig,
    release: sentryRelease ?? undefined,
    ...sentryConfig,
    dsn:
      sentryConfig.primaryDsn ||
      'https://fb3a44a2f78e4676911d7f323294dcb9@sentry.io/1273292',
    enableLogs: true,
    beforeSendLog(log) {
      // Step 1: Remote Config enabled check
      if (!_logEnabled) return null;

      // Step 2: Skip level check for manually sent debug logs (enableSentryDebug)
      const isManualSendDebug = log.attributes?.['logInfo.namespace'] != null;
      const isBelowMinSendLevel = (LOG_LEVEL_ORDER[log.level] ?? 0) < _logLevel;
      if (!isManualSendDebug && isBelowMinSendLevel) return null;

      return log;
    },
    beforeSendTransaction(event) {
      // when the event has cancelled attribute, it means the span is cancelled, so we don't need to send the event
      if (event?.contexts?.trace?.data?.cancelled) return null;
      return event;
    },
  };

  init(sentryOptions);

  // second sentry
  // refer to https://docs.sentry.io/platforms/javascript/best-practices/multiple-sentry-instances/
  const sentry2Options = {
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    integrations: getDefaultIntegrations().filter(defaultIntegration => {
      return !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(
        defaultIntegration.name
      );
    }),
    ...commonSentryConfig,
    release: sentryRelease ?? undefined,
    ...sentryConfig,
    dsn:
      sentryConfig.secondaryDsn ||
      'https://6215b20088bd4e34b025b17e96a65119@o37462.ingest.sentry.io/4504162520137728',
    sampleRate: 1,
    beforeSend(event) {
      if (event.tags.bug_report) return event;
      return null;
    },
  };
  const client = new BrowserClient(sentry2Options);
  const scope = new Scope();
  scope.setClient(client);
  client.init();
  Sentry2Scope = scope;

  if (_sessionId) {
    Sentry?.setTag('session_id', _sessionId);
    Sentry2Scope?.setTag('session_id', _sessionId);
  }

  return Sentry;
};

export const updateSentryConfig = async ({ sentryConfig }) => {
  const sentry = await getSentry();
  sentry.setLevel?.(sentryConfig.level);
  sentry.setTag?.('environment', sentryConfig.environment);
};

export const getSentry = async () => {
  if (getIsInServiceWorker()) {
    return Sentry || self._SENTRY_INSTANCE_;
  }

  return Sentry || initSentry();
};

/**
 * refer to: https://github.com/getsentry/sentry-javascript/blob/7.0.0/MIGRATION.md#severity-severitylevel-and-severitylevels
 * The new version of sentry deprecated the Severity enum
 * So extract it from old version of sentry
 */
export const Severity = {
  /** JSDoc */
  Fatal: 'fatal',
  /** JSDoc */
  Error: 'error',
  /** JSDoc */
  Warning: 'warning',
  /** JSDoc */
  Log: 'log',
  /** JSDoc */
  Info: 'info',
  /** JSDoc */
  Debug: 'debug',
};

/**
 * return the second sentry for bug report
 * @returns { import('@sentry/browser').Hub }
 */
export const getSentry2Scope = () => {
  return Sentry2Scope;
};

/**
 * Replace some strings that would by default be scrubbing by Sentry.
 * @param {string} {text} - the data to be processed
 * @return {string} processed text.
 */
export const replaceStringsToAvoidDataScrubbing = (text = '') => {
  const sensitiveStrings = [
    ['credentials', 'credentia1s'],
    ['password', 'passw0rd'],
    ['register', 'reg1ster'],
    ['private', 'pr1vate'],
    ['api_key', 'ap1_key'],
    ['config', 'c0nfig'],
    ['login', 'l0gin'],
    ['token', 't0ken'],
    ['auth', 'avth'],
  ];
  return sensitiveStrings.reduce((result, [string, replacement]) => {
    const regex = new RegExp(string, 'gi');
    return result.replace(regex, replacement);
  }, text);
};

// --- Structured Logging helper ---
const STRUCTURED_LOG_LEVELS = [
  'trace',
  'debug',
  'info',
  'warn',
  'error',
  'fatal',
];

// Sentry Structured Logging only accepts string | number | boolean as attribute values.
// This function auto-converts objects/arrays and applies data scrubbing on strings.
const sanitizeAttributes = attributes => {
  if (!attributes || typeof attributes !== 'object') return {};

  const result = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value == null) continue;

    if (typeof value === 'number' || typeof value === 'boolean') {
      result[key] = value;
      continue;
    }

    // object/array → JSON.stringify + sanitize
    if (typeof value === 'object') {
      try {
        result[key] = replaceStringsToAvoidDataScrubbing(JSON.stringify(value));
      } catch {
        result[key] = '[Unserializable]';
      }
      continue;
    }

    // string → sanitize
    result[key] = replaceStringsToAvoidDataScrubbing(String(value));
  }
  return result;
};

const getStructuredLogger = async () => {
  if (getIsInServiceWorker()) {
    return (Sentry || globalThis.self?._SENTRY_INSTANCE_)?.logger;
  }

  if (!sentryLoader) return null;
  const sentryModule = await sentryLoader();
  return sentryModule?.logger;
};

const getBrowserLogMeta = async () => {
  if (!getIsInBrowserMainThread()) return {};
  if (!logMetaLoader) return {};

  try {
    return await logMetaLoader();
  } catch {
    return {};
  }
};

const createLogMethod = level => {
  return (message, attributes) => {
    Promise.all([getStructuredLogger(), getBrowserLogMeta()])
      .then(([logger, meta]) => {
        if (logger && typeof logger[level] === 'function') {
          const sanitizedAttributes = sanitizeAttributes({
            ...meta,
            ...attributes,
          });
          logger[level](message, sanitizedAttributes);
        }
      })
      .catch(() => {});
  };
};

export const sentryLogger = {};
STRUCTURED_LOG_LEVELS.forEach(level => {
  sentryLogger[level] = createLogMethod(level);
});

// --- Sentry Log Capture ---
//
// startSentryLogCapture({ captureName }) to start.
// endSentryLogCapture({ captureName }) to stop.
// Only pino logs during this window are sent to Sentry.
// Auto-ends after timeout (default 30s).
// Remote Config (SENTRY_LOG_CAPTURE_NAMES) controls which names are allowed.
//
//   startCapture()                    endCapture()
//        |                                 |
//        v                                 v
//   ─────[===== logs sent to Sentry =====]─────
//        |<──── timeout auto-end (30s) ────>|

const DEFAULT_CAPTURE_TIMEOUT_MSEC = 30_000; // 30 seconds

let allowedCaptureNames = process.env.SENTRY_LOG_CAPTURE_NAMES || [];
let captureTimeoutMsec =
  Number(process.env.SENTRY_LOG_CAPTURE_TIMEOUT_MSEC) ||
  DEFAULT_CAPTURE_TIMEOUT_MSEC;

// captureName → { captureId, startTime, timeoutId, attributes }
// e.g. 'livestream-send-gift' → {
//   captureId: 'livestream-send-gift-1711849200123',
//   startTime: 1711849200123,
//   timeoutId: 42,
//   attributes: { streamId: '123', giftId: '456' },
// }
const activeCaptureMap = new Map();

export const updateSentryLogCaptureConfig = ({
  allowedCaptureNames: newAllowedNames,
  timeoutMsec,
} = {}) => {
  allowedCaptureNames = newAllowedNames || [];
  if (timeoutMsec != null) captureTimeoutMsec = timeoutMsec;
  // End captures that are no longer allowed
  for (const [captureName] of activeCaptureMap) {
    if (!allowedCaptureNames.includes(captureName)) {
      endSentryLogCapture({ captureName });
    }
  }
};

export const startSentryLogCapture = ({
  captureName,
  attributes,
  options = {},
} = {}) => {
  // Step 0: Skip on server
  if (typeof window === 'undefined') return null;

  // Step 1: Check if this name is allowed by Remote Config
  if (!allowedCaptureNames.includes(captureName)) return null;

  // Step 2: Parse options
  const { timeoutMs = captureTimeoutMsec } = options;

  // Step 3: If already running, reset timer
  const existing = activeCaptureMap.get(captureName);
  if (existing) {
    clearTimeout(existing.timeoutId);
    existing.timeoutId = setTimeout(() => {
      endSentryLogCapture({ captureName });
    }, timeoutMs);
    return existing.captureId;
  }

  // Step 4: Create new capture
  const captureId = `${captureName}-${Date.now()}`;
  const timeoutId = setTimeout(() => {
    endSentryLogCapture({ captureName });
  }, timeoutMs);

  activeCaptureMap.set(captureName, {
    captureId,
    startTime: Date.now(),
    timeoutId,
    attributes: attributes || {},
  });

  return captureId;
};

export const endSentryLogCapture = ({ captureName } = {}) => {
  const capture = activeCaptureMap.get(captureName);
  if (!capture) return;

  clearTimeout(capture.timeoutId);
  activeCaptureMap.delete(captureName);
};

// --- Pino → Sentry log forwarding ---
//
// Manual debug usage:
//   Console: enableSentryDebug('player*')
//   URL:     ?sentryDebug=player*
//   Disable: disableSentryDebug()

// Namespace filter (wildcard: 'player*', '-player:internal*')
const sentryDebugEnabledNamespaces = [];
const sentryDebugSkipNamespaces = [];

const parseSentryDebugNamespaces = namespaces => {
  const parts = namespaces.split(/[\s,]+/).filter(Boolean);
  for (const part of parts) {
    if (part[0] === '-') {
      sentryDebugSkipNamespaces.push(
        new RegExp('^' + part.slice(1).replace(/\*/g, '.*?') + '$')
      );
    } else {
      sentryDebugEnabledNamespaces.push(
        new RegExp('^' + part.replace(/\*/g, '.*?') + '$')
      );
    }
  }
};

const clearSentryDebugNamespaces = () => {
  sentryDebugEnabledNamespaces.length = 0;
  sentryDebugSkipNamespaces.length = 0;
};

const isSentryDebugEnabled = name =>
  !sentryDebugSkipNamespaces.some(re => re.test(name)) &&
  sentryDebugEnabledNamespaces.some(re => re.test(name));

const getRuntimeLogAttributes = () => {
  const navigator = globalThis.navigator;

  if (getIsInBrowserMainThread()) {
    const href = globalThis.window?.location?.href;
    return {
      'logInfo.runtime': 'browser',
      ...(href ? { 'logInfo.href': href } : {}),
    };
  }

  if (getIsInServiceWorker()) {
    const href = globalThis.location?.href;
    const scope = globalThis.registration?.scope;

    return {
      'logInfo.runtime': 'service-worker',
      ...(href ? { 'logInfo.workerHref': href } : {}),
      ...(scope ? { 'logInfo.serviceWorkerScope': scope } : {}),
      ...(navigator?.userAgent
        ? { 'metadata.user_agent': navigator.userAgent }
        : {}),
      ...(navigator?.language
        ? { 'metadata.language': navigator.language }
        : {}),
    };
  }

  return {
    'logInfo.runtime': 'unknown',
  };
};

// Return active captures that allow this namespace to be uploaded.
const getActiveCaptures = namespace => {
  return Array.from(activeCaptureMap.entries()).filter(([captureName]) => {
    const allowedNamespaces = LOG_CAPTURE_ALLOWED_NAMESPACES[captureName] || [];
    return allowedNamespaces.some(
      ns => namespace === ns || namespace.startsWith(`${ns}:`)
    );
  });
};

// Convert pino log to Sentry structured log and send to Sentry.
const sendPinoLogToSentry = (namespace, pinoArgs) => {
  // Step 1: Decide if we should send this log to Sentry
  //   manual debug on  → send (developer turned on enableSentryDebug in console)
  //   active capture   → send (code called startSentryLogCapture)
  //   none matched     → don't send, including error namespaces
  const isError = namespace.includes(':error:') || namespace.endsWith(':error');
  const activeCaptures = getActiveCaptures(namespace);
  const hasActiveCapture = activeCaptures.length > 0;
  if (!isSentryDebugEnabled(namespace) && !hasActiveCapture) return;

  const logLevel = isError ? 'error' : hasActiveCapture ? 'info' : 'debug';

  // Step 2: Get the message text (always the first arg)
  //   debug('gift [hat] POST start', { giftId }) → message = 'gift [hat] POST start'
  const message =
    typeof pinoArgs[0] === 'string' ? pinoArgs[0] : String(pinoArgs[0]);

  // Step 3: Build the data for Sentry log
  const attributes = {
    'logInfo.namespace': namespace,
    ...getRuntimeLogAttributes(),
    textKey: message,
    content: message,
  };

  // Step 3a: If there are extra string args in the middle, join them
  //   debug('msg', 'GET /api', { data }) → logInfo.details = 'GET /api'
  if (pinoArgs.length > 2) {
    const middleMessages = pinoArgs
      .slice(1, -1)
      .filter(a => typeof a === 'string');
    if (middleMessages.length > 0) {
      attributes['logInfo.details'] = middleMessages.join(' ');
    }
  }

  // Step 3b: The last arg is usually the data object or an Error
  //   debug('msg', { giftId: '123' }) → saved as JSON in attachedMessage
  //   debug('msg', new Error('fail')) → saved as errorName + errorMessage
  const lastArg =
    pinoArgs.length > 1 ? pinoArgs[pinoArgs.length - 1] : undefined;

  if (lastArg instanceof Error) {
    attributes['logInfo.errorName'] = lastArg.name;
    attributes['logInfo.errorMessage'] = lastArg.message;
  } else if (lastArg != null && typeof lastArg === 'object') {
    try {
      const safeClonedLastArg = safeStructuredClone({ value: lastArg });
      attributes['attachedMessage'] = JSON.stringify({
        ...safeClonedLastArg,
        message,
      });
    } catch {
      attributes['attachedMessage'] = '[Unserializable]';
    }
  }

  // Step 4: Upload capture info to Sentry
  if (hasActiveCapture) {
    for (const [captureName, captureData] of activeCaptures) {
      attributes[`captureName.${captureName}`] = true;
      attributes[`captureId.${captureName}`] = captureData.captureId;
    }
  }

  // Step 5: Send to Sentry
  sentryLogger[logLevel](message, attributes);
};

if (getIsInBrowserMainThread() || getIsInServiceWorker()) {
  addPinoLogListener(sendPinoLogToSentry);
}

// Console API:
//   enableSentryDebug('player*')
//   enableSentryDebug('gift*,payment*')
//   disableSentryDebug()
globalThis.enableSentryDebug = namespaces => {
  clearSentryDebugNamespaces();
  parseSentryDebugNamespaces(namespaces);
  try {
    sessionStorage.setItem('sentryDebug', namespaces);
    // eslint-disable-next-line
  } catch (_) {}
};

globalThis.disableSentryDebug = () => {
  clearSentryDebugNamespaces();
  try {
    sessionStorage.removeItem('sentryDebug');
    // eslint-disable-next-line
  } catch (_) {}
  removePinoLogListener(sendPinoLogToSentry);
};

// Read URL / sessionStorage on load
if (typeof window !== 'undefined') {
  try {
    const url = new URL(location.href);
    const urlSentryDebug = url.searchParams.get('sentryDebug');
    const sessionSentryDebug = sessionStorage.getItem('sentryDebug');
    if (urlSentryDebug) {
      parseSentryDebugNamespaces(urlSentryDebug);
      sessionStorage.setItem('sentryDebug', urlSentryDebug);
    } else if (sessionSentryDebug) {
      parseSentryDebugNamespaces(sessionSentryDebug);
    }
    // eslint-disable-next-line
  } catch (_) {}
}
